import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Shield, GraduationCap, UserCheck, ArrowRight, Settings } from "lucide-react";

const AdminDashboard = () => {
  const [stats, setStats] = useState({ total: 0, students: 0, growlearn: 0, aca: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      const { data: roles } = await supabase.from("user_roles").select("role");
      if (roles) {
        setStats({
          total: roles.length,
          students: roles.filter(r => r.role === "student").length,
          growlearn: roles.filter(r => r.role === "growlearn").length,
          aca: roles.filter(r => r.role === "aca_member").length,
        });
      }
    };
    fetchStats();
  }, []);

  const cards = [
    { label: "Total Users",  value: stats.total,    icon: Users,         border: "stat-card-border-blue",   iconBg: "bg-primary/10",      iconColor: "text-primary" },
    { label: "Students",     value: stats.students,  icon: GraduationCap, border: "stat-card-border-green",  iconBg: "bg-success/10",      iconColor: "text-success" },
    { label: "GrowLearn",    value: stats.growlearn, icon: UserCheck,     border: "stat-card-border-amber",  iconBg: "bg-warning/10",      iconColor: "text-warning" },
    { label: "ACA Members",  value: stats.aca,       icon: Shield,        border: "stat-card-border-purple", iconBg: "bg-purple-500/10",   iconColor: "text-purple-500" },
  ];

  const quickLinks = [
    { label: "Manage Students", desc: "View all students and application status", href: "/admin/students", icon: GraduationCap, iconBg: "bg-primary/10", iconColor: "text-primary" },
    { label: "Manage Users",    desc: "Create accounts and assign roles",          href: "/admin/users",    icon: Settings,      iconBg: "bg-warning/10", iconColor: "text-warning" },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Hero */}
        <div className="rounded-2xl gradient-hero p-8 text-primary-foreground shadow-xl">
          <h2 className="text-2xl font-bold tracking-tight">Admin Dashboard</h2>
          <p className="mt-1.5 text-primary-foreground/70 text-sm">System overview and user management</p>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c, i) => (
            <Card key={c.label} className={`glass-premium card-hover ${c.border} transition-all duration-300`} style={{ animationDelay: `${i * 80}ms` }}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{c.label}</CardTitle>
                <div className={`w-9 h-9 rounded-xl ${c.iconBg} flex items-center justify-center ${c.iconColor}`}>
                  <c.icon className="h-5 w-5" />
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-foreground">{c.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Links */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-3">Quick Actions</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {quickLinks.map((ql) => (
              <Link key={ql.href} to={ql.href}>
                <Card className="glass-premium card-hover cursor-pointer group">
                  <CardContent className="p-5 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl ${ql.iconBg} flex items-center justify-center`}>
                        <ql.icon className={`h-5 w-5 ${ql.iconColor}`} />
                      </div>
                      <div>
                        <p className="font-semibold text-foreground text-sm">{ql.label}</p>
                        <p className="text-xs text-muted-foreground">{ql.desc}</p>
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
